import PropTypes from "prop-types"
import React from "react"
import {
  TextChart,
  TextChartMajor,
} from "components/widgets/ScorePage/widgets/TextCharts"
import { useTranslation } from "react-i18next"
import NodeOrganization from "services/organizations/NodeOrganization"
import HelpWidgetChartInteractiveMolecule from "views/molecules/interactive/HelpWidgetChartInteractiveMolecule"
import Unit from "views/atoms/reports/Unit"
import { safePercentage } from "utils/safeMath"
import { round } from "services/units/round"
import { convertValueToReadable } from "services/units/valueConversion"
import { CO2_EMISSION_UNITS, T_CO2E } from "services/units/units"
import ChartWithBadgeWrapperMolecule from "views/molecules/charts/ChartWithBadgeWrapperMolecule"

const TextChartODD13GHGEmissionsMolecule = ({
  score,
  ghgEmissions = 0,
  waterConsumption,
  hidePercentage,
  trendBadge,
}) => {
  const { t } = useTranslation()

  const { value, unit } = convertValueToReadable(
    ghgEmissions,
    T_CO2E,
    CO2_EMISSION_UNITS,
    (value) => round(value)
  )

  const content = {
    kpi: value,
    kpi_unit: <Unit unit={t(`units:units.emission.${unit}`)} />,
    kpi_text: t(`score:score.13.chartGHGEmissions.text`),
    translationPrefix_chartX: `score:score.13.chartGHGEmissions.`,
  }

  if (ghgEmissions === score?.odd13_it_domain_co2 && !hidePercentage) {
    content.reference = round(safePercentage(ghgEmissions, score?.odd13_CO2))
    content.reference_unit = "%"
    content.reference_text = t(
      `score:score.13.chartGHGEmissions.reference_text`
    )
  }

  if (waterConsumption) {
    content.reference = waterConsumption
    content.reference_unit = <Unit unit={t(`units:units.volume.m3_unit`)} />
    content.reference_text = t(`score:score.13.chartWaterConsumption.text`)
  }

  if (score?.odd13_global_node_parent_score_defined && !hidePercentage) {
    const odd13_co2_share_vs_parent = score?.odd13_co2_share_vs_parent || 0

    content.reference = odd13_co2_share_vs_parent.percent
    content.reference_unit = (
      <Unit unit={t(`score:score.13.chartGHGEmissions.unit`)} />
    )
    content.reference_text = NodeOrganization.getName(
      odd13_co2_share_vs_parent?.name,
      odd13_co2_share_vs_parent?.type
    )
  }

  return (
    <HelpWidgetChartInteractiveMolecule
      text={t(`score:score.13.chartGHGEmissions.help`)}
    >
      <ChartWithBadgeWrapperMolecule trendBadge={trendBadge} color="auto">
        <TextChart content={<TextChartMajor content={content} noSubtextHover />} />
      </ChartWithBadgeWrapperMolecule>
    </HelpWidgetChartInteractiveMolecule>
  )
}

TextChartODD13GHGEmissionsMolecule.propTypes = {
  score: PropTypes.object.isRequired,
  ghgEmissions: PropTypes.string.isRequired,
  waterConsumption: PropTypes.number,
  hidePercentage: PropTypes.bool,
  trendBadge: PropTypes.object,
}

TextChartODD13GHGEmissionsMolecule.defaultProps = {
  waterConsumption: null,
  hidePercentage: false,
  trendBadge: null,
}

export default TextChartODD13GHGEmissionsMolecule